import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Check,
} from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'
import {
  marketingColors as colors,
  marketingBrand,
  PAPER,
  BOOKING_CALENDAR_URL,
  marketingEase as ease,
} from '../lib/marketingBrand'
import {
  ACCENT,
  HeroEmboss,
  HudsonOfferBanner,
  MKEyebrow,
  MKImageFrame,
  MKRule,
  PaperTextureOverlay,
  SketchBreak,
} from '../components/marketing/MarketingChrome'

const IMG_BASE =
  'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/'

const img = (name: string) => `${IMG_BASE}${encodeURIComponent(name)}`

/* ─── Hero ─── */

const Hero: React.FC = () => (
  <section className="relative min-h-screen flex items-center overflow-hidden" style={{ backgroundColor: PAPER }}>
    <img
      src={img('coupleholdingsoldsign.jpg')}
      alt=""
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0" style={{ background: `linear-gradient(105deg, ${PAPER}f0 0%, ${PAPER}e0 38%, ${PAPER}70 65%, transparent 100%)` }} />
    <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${PAPER}e8 0%, transparent 30%, transparent 75%, ${PAPER}f0 100%)` }} />
    <HeroEmboss />

    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 xl:px-20 py-32 w-full">
      <div className="max-w-2xl">
        <motion.div initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }} transition={{ duration: 0.9, delay: 0.15, ease }} className="origin-left mb-8">
          <MKRule wide />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease }}
          className="font-serif mb-6"
          style={{
            fontSize: 'clamp(2.4rem, 5vw, 3.8rem)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: colors.tealDark,
          }}
        >
          Your New Home Should Feel
          <br />
          Like a Fresh Start.
          <br />
          <span className="italic" style={{ color: colors.copper }}>
            Not a Question Mark.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease }}
          className="text-lg lg:text-xl leading-[1.85] max-w-xl mb-10 font-sans"
          style={{ color: colors.drillbitSkies }}
        >
          Hudson My Home™ partners with real estate agents across the Des Moines
          metro to provide impactful closing gifts, pre-listing inspections that
          protect your deals, and home care that keeps your clients coming back to
          you.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease }}
        >
          <a
            href={BOOKING_CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase text-white transition-all duration-500 hover:brightness-110"
            style={{ backgroundColor: colors.copper }}
          >
            Book a 15-Minute Demo
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </div>
  </section>
)

/* ─── At a glance: how we help agents ─── */

const ProgramsForAgents: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })
  const cards = [
    {
      title: 'Closing & move-in',
      body: 'MyHome SmartStart™ — one visit to reset locks, detectors, filters, and core systems. Your buyers start safe; you stay memorable.',
      href: '#agent-smartstart-detail',
      cta: 'SmartStart on this page',
      art: ACCENT.iconKey,
    },
    {
      title: 'Before the sign goes up',
      body: 'Pre-listing inspections and documentation so sellers fix issues early and deals stay together.',
      href: '/pricing#prelisting',
      cta: 'See pre-listing pricing',
      art: ACCENT.myhomeScoreReport,
    },
    {
      title: 'After day one',
      body: 'Hudson Membership for clients who want year-round proactive care—not another vendor list.',
      href: '/membership',
      cta: 'Membership overview',
      art: ACCENT.hudsonConsultation,
    },
  ]
  return (
    <section ref={ref} className="relative py-20 md:py-24 overflow-hidden" style={{ backgroundColor: PAPER }}>
      <PaperTextureOverlay opacity={0.03} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.65, ease }}
          className="font-serif text-center max-w-3xl mx-auto mb-4"
          style={{
            fontSize: 'clamp(1.65rem, 3.5vw, 2.5rem)',
            lineHeight: 1.12,
            letterSpacing: '-0.02em',
            color: colors.tealDark,
          }}
        >
          How Hudson fits your business
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, delay: 0.08, ease }}
          className="text-center font-sans text-base max-w-2xl mx-auto mb-12 leading-[1.85]"
          style={{ color: colors.drillbitSkies }}
        >
          Three entry points—pick what matches the client, or walk them through all three over time. Same Hudson standard
          everywhere.
        </motion.p>
        <div className="grid md:grid-cols-3 gap-8">
          {cards.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 32 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.1 + i * 0.1, ease }}
              className="bg-white p-8 lg:p-10 flex flex-col"
              style={{ borderBottom: `3px solid ${colors.copper}` }}
            >
              <img
                src={c.art}
                alt=""
                className="w-20 h-20 object-contain mb-6"
                style={{ mixBlendMode: 'multiply', opacity: 0.85 }}
                loading="lazy"
              />
              <h3 className="font-serif text-xl mb-2" style={{ color: colors.tealDark }}>
                {c.title}
              </h3>
              <div className="w-10 h-px mb-4" style={{ backgroundColor: colors.copper }} />
              <p className="font-sans text-sm leading-relaxed flex-1 mb-6" style={{ color: colors.drillbitSkies }}>
                {c.body}
              </p>
              <a
                href={c.href}
                className="group inline-flex items-center gap-2 font-sans text-sm font-semibold uppercase tracking-[0.06em]"
                style={{ color: colors.copper }}
              >
                {c.cta}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── Stand Out ─── */

const StandOutSection: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="relative py-28 md:py-36 lg:py-48 overflow-hidden" style={{ backgroundColor: colors.cream }}>
      <PaperTextureOverlay opacity={0.03} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease }}
          >
            <h2
              className="font-serif mb-6"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                color: colors.nearBlack,
              }}
            >
              Stand Out at Every Closing.
              <br />
              <span className="italic" style={{ color: colors.copper }}>
                Be the Agent They Remember.
              </span>
            </h2>
            <p className="text-lg leading-[1.85] mb-6 font-sans" style={{ color: colors.drillbitSkies }}>
              Give your buyers something better than a gift basket. Give them a Hudson. A MyHome SmartStart&#8482; is the kind of gift that makes your buyers feel taken care of from day one — and keeps your name on their mind for years.
            </p>
            <p className="text-lg leading-[1.85] mb-10 font-sans" style={{ color: colors.drillbitSkies }}>
              Partner with Hudson My Home™ to offer something no other agent in the metro can: professional home management that starts the moment your client gets the keys.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={BOOKING_CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-white transition-all duration-300 hover:opacity-90"
                style={{ backgroundColor: colors.copper }}
              >
                Book a 15-Minute Demo
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium border border-gray-300 transition-colors hover:bg-gray-50"
                style={{ color: colors.nearBlack }}
              >
                See Agent Pricing
              </Link>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, ease, delay: 0.2 }}
          >
            <MKImageFrame
              src={ACCENT.portfolioHandoff}
              alt="Keys and portfolio at closing"
              aspect="aspect-[4/3]"
            />
          </motion.div>
        </div>
      </div>
    </section>
  )
}

/* ─── Problem ─── */

const ProblemSection: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="relative py-28 md:py-36 lg:py-48 overflow-hidden" style={{ backgroundColor: PAPER }}>
      <PaperTextureOverlay opacity={0.03} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 60 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease }}
              className="font-serif mb-4"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                color: colors.nearBlack,
              }}
            >
              Moving In Is Exciting. Endless To-Do
              <br />
              Lists Are Not.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15, ease }}
              className="font-serif italic text-xl lg:text-2xl"
              style={{ color: colors.teal }}
            >
              We Handle the Part Nobody Looks
              <br />
              Forward To.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease }}
            className="space-y-6 text-lg leading-relaxed font-sans"
            style={{ color: colors.drillbitSkies }}
          >
            <p>
              You don't know who still has access. You don't know what was last
              serviced or when. You don't know what the previous owners missed,
              ignored, or left behind. Moving in should feel like a true reset, but
              it rarely does without the right help.
            </p>
            <p>
              MyHome SmartStart™ takes care of everything so you can settle in with
              confidence instead of wondering what you're inheriting.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

/* ─── SmartStart Packages ─── */

const essentialChecklist = [
  'New locks and rekeyed garage access so only you have entry',
  'Smoke and carbon monoxide detectors replaced',
  'Full home systems orientation so you know where everything is',
  'Dryer vents, HVAC filters, and fridge water filter replaced',
  'Exhaust fans, under-sink plumbing, and caulking inspected and refreshed',
  'Completed in a single two-hour visit',
]

const deluxeChecklist = [
  'Everything in the Essential SmartStart',
  'Water heater flushed and temperature confirmed',
  'Windows, door seals, and weather stripping checked and replaced as needed',
  'Major appliances deep cleaned and reset',
  'Full MyHome Snapshot™ with documented home condition and maintenance priorities',
  'Completed in a single four-hour visit',
]

const SmartStartSection: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section
      ref={ref}
      id="agent-smartstart-detail"
      className="py-28 md:py-36 lg:py-48 scroll-mt-28"
      style={{ backgroundColor: colors.cream }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="text-center max-w-3xl mx-auto mb-6">
          <motion.h2
            initial={{ opacity: 0, y: 60 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease }}
            className="font-serif mb-4"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: colors.nearBlack,
            }}
          >
            Immediate Confidence. Complete Peace of Mind.
          </motion.h2>

          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <a
              href={BOOKING_CALENDAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase transition-colors duration-200"
              style={{ color: colors.copper }}
            >
              Book a 15-Minute Demo
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2, ease }}
          className="text-lg leading-relaxed max-w-3xl mx-auto text-center mb-16 font-sans"
          style={{ color: colors.drillbitSkies }}
        >
          Most new homeowners spend their first weeks hoping everything works. The
          MyHome SmartStart™ removes that uncertainty entirely. One visit handles
          the safety essentials, hidden risks, and interior details that get
          overlooked during a move, so your home is truly yours from the moment you
          walk in.
        </motion.p>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Essential */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.25, ease }}
            className="bg-white p-8 lg:p-10 flex flex-col"
          >
            <h3
              className="font-serif text-2xl lg:text-3xl mb-2"
              style={{ color: colors.nearBlack }}
            >
              Essential MyHome SmartStart™
            </h3>
            <p
              className="italic text-base mb-6"
              style={{ color: colors.copper }}
            >
              The essentials, handled before you settle in.
            </p>

            <div
              className="text-base leading-relaxed mb-8 space-y-4 font-sans"
              style={{ color: colors.drillbitSkies }}
            >
              <p>
                New exterior locks, rekeyed garage access, fresh smoke and CO
                detectors, and a full orientation of your home's systems so you
                know exactly where everything is. Dryer vents, HVAC filters, and
                fridge water filters are handled. Exhaust fans, under-sink
                plumbing, and caulking are inspected and refreshed.
              </p>
              <p>
                You receive a digital confirmation of every completed item before
                we leave.
              </p>
            </div>

            <div className="space-y-3 mb-8">
              {essentialChecklist.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <Check
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    style={{ color: colors.teal }}
                  />
                  <span className="text-sm lg:text-base text-gray-700">
                    {item}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-auto">
              <Link
                to="/pricing"
                className="group inline-flex items-center gap-2 px-6 py-3 text-base font-medium text-white transition-all duration-300 hover:opacity-90"
                style={{ backgroundColor: colors.teal }}
              >
                See Pricing
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>

          {/* Deluxe */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.35, ease }}
            className="p-8 lg:p-10 flex flex-col"
            style={{ backgroundColor: colors.tealDark }}
          >
            <h3 className="font-serif text-2xl lg:text-3xl text-white mb-2">
              Deluxe MyHome SmartStart™
            </h3>
            <p className="italic text-base mb-6" style={{ color: colors.copper }}>
              Everything in the Essential, plus a deeper reset that leaves nothing
              unaddressed.
            </p>

            <div className="text-base leading-relaxed mb-8 space-y-4 text-white/70">
              <p>
                Your water heater is flushed, windows and door seals are checked,
                and major appliances are deep cleaned and brought to a known,
                reliable baseline.
              </p>
              <p>
                At the end of the visit you receive a full MyHome Snapshot™
                documenting your home's condition, maintenance priorities, and an
                optional path to Hudson membership.
              </p>
              <div className="px-4 py-3 rounded-md" style={{ backgroundColor: 'rgba(171, 147, 129, 0.2)' }}>
                <p className="text-sm font-semibold" style={{ color: colors.copper }}>
                  $100 Credit Included — Every Deluxe SmartStart includes a $100 credit toward any future Hudson My Home™ service.
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-8">
              {deluxeChecklist.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <Check
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    style={{ color: colors.copper }}
                  />
                  <span className="text-sm lg:text-base text-white/80">
                    {item}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-auto">
              <Link
                to="/pricing"
                className="group inline-flex items-center gap-2 px-6 py-3 text-base font-medium transition-all duration-300 hover:opacity-90"
                style={{ backgroundColor: colors.copper, color: '#fff' }}
              >
                See Pricing
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

/* ─── How It Works ─── */

const steps = [
  {
    num: '01',
    title: 'Choose Your Service',
    body: 'Select the Essential or Complete SmartStart. Not sure which is right? We can help you decide in a quick call.',
  },
  {
    num: '02',
    title: 'We Come to You',
    body: 'Your Hudson professional arrives and works through every item from top to bottom. Security, safety, hidden risks, and interior details — all documented as we go.',
  },
  {
    num: '03',
    title: 'Your Home Is Ready',
    body: 'Every item confirmed, every concern addressed. Your digital report tells you exactly what was handled and what to keep an eye on. Your home is yours now. Fully.',
  },
]

const HowItWorks: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="py-28 md:py-36 lg:py-48" style={{ backgroundColor: PAPER }}>
      <PaperTextureOverlay opacity={0.025} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease }}
          className="text-center mb-16"
        >
          <MKEyebrow className="justify-center mb-4">How It Works</MKEyebrow>
          <MKRule className="mx-auto" />
        </motion.div>

        <div className="grid md:grid-cols-3 gap-10 lg:gap-16">
          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 + i * 0.12, ease }}
            >
              <span
                className="block font-serif text-5xl lg:text-6xl font-normal mb-4"
                style={{ color: colors.copper }}
              >
                {step.num}
              </span>
              <h3
                className="font-serif text-xl lg:text-2xl mb-3"
                style={{ color: colors.tealDark }}
              >
                {step.title}
              </h3>
              <p className="text-base lg:text-lg leading-relaxed" style={{ color: colors.drillbitSkies }}>
                {step.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── Categories ─── */

const categories = [
  {
    art: '/illustrations/clipboard-checklist.png',
    title: 'Interior Refresh',
    body: 'All hidden corners and caulking are inspected, cleaned, and refreshed so your home feels truly move-in ready.',
  },
  {
    art: '/illustrations/icon-key.png',
    title: 'Safety and Security',
    body: "With new locks and updated safety measures, you'll know exactly who has access to your home and exactly what to do in any situation.",
  },
  {
    art: '/illustrations/hudson-hvac.png',
    title: 'Mechanical and Water Protection',
    body: "Heat and humidity put pressure on your home's systems. We check cooling efficiency, ventilation, moisture control, and appliances running harder than usual.",
  },
  {
    art: '/illustrations/tools-flatlay.png',
    title: 'The Things Nobody Gets To',
    body: "The unglamorous checklist that sits at the back of every new homeowner's mind and almost never gets done. Handled.",
  },
]

const CategoriesSection: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section
      ref={ref}
      className="py-28 md:py-36 lg:py-48"
      style={{ backgroundColor: colors.cream }}
    >
      <PaperTextureOverlay opacity={0.03} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={inView ? { opacity: 1, scaleX: 1 } : {}}
          transition={{ duration: 0.85, ease }}
          className="origin-center mx-auto mb-8"
        >
          <MKRule className="mx-auto" />
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 60 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease }}
          className="font-serif text-center max-w-3xl mx-auto mb-16"
          style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: colors.nearBlack,
          }}
        >
          Everything You&apos;ve Been Putting Off.
          <br />
          <span className="italic" style={{ color: colors.teal }}>
            Handled Before You Even Think About It.
          </span>
        </motion.h2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((cat, i) => (
            <motion.div
              key={cat.title}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 + i * 0.1, ease }}
              className="bg-white p-8"
              style={{ borderBottom: `3px solid ${colors.copper}` }}
            >
              <img
                src={cat.art}
                alt=""
                className="w-16 h-16 object-contain mb-6"
                style={{ mixBlendMode: 'multiply', opacity: 0.85 }}
                loading="lazy"
              />
              <h3
                className="font-serif text-lg mb-2"
                style={{ color: colors.tealDark }}
              >
                {cat.title}
              </h3>
              <p className="text-sm lg:text-base leading-relaxed" style={{ color: colors.drillbitSkies }}>
                {cat.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── Membership Upsell ─── */

const MembershipUpsell: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="relative py-28 md:py-36 lg:py-48 overflow-hidden">
      <img
        src={img('keyindoor.jpg')}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D3135]/90 via-[#0D3135]/80 to-[#0D3135]/60" />

      <div className="relative max-w-[800px] mx-auto px-6 md:px-10 lg:px-16 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 60 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease }}
          className="font-serif text-white mb-6"
          style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
          }}
        >
          Want Your Home Taken Care of
          <br />
          <span className="italic" style={{ color: colors.copper }}>
            Beyond Day One?
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.15, ease }}
          className="text-lg lg:text-xl text-white/70 leading-relaxed mb-10"
        >
          MyHome SmartStart™ gets your home off to the right start. A Hudson My
          Home™ membership keeps it there — every season, proactively handled,
          entirely taken care of. Your SmartStart credit applies directly toward
          your first month.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.25, ease }}
        >
          <Link
            to="/membership"
            className="group inline-flex items-center gap-3 px-8 py-4 text-lg font-medium text-white transition-all duration-300 hover:opacity-90"
            style={{ backgroundColor: colors.copper }}
          >
            Learn About Membership
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

/* ─── Page ─── */

const PartnersPage: React.FC = () => (
  <div className="min-h-screen" style={{ backgroundColor: marketingBrand.tapeTurquoise }}>
    <MetaTags
      title="Real Estate Agent Partnerships | Hudson My Home"
      description="Give your buyers a closing gift they'll use every day. Partner with Hudson My Home to offer new homeowners a dedicated home professional from day one."
    />
    <Navbar />
    <HudsonOfferBanner />
    <Hero />
    <ProgramsForAgents />
    <SketchBreak />
    <StandOutSection />
    <ProblemSection />
    <SmartStartSection />
    <HowItWorks />
    <CategoriesSection />
    <MembershipUpsell />
    <Footer />
  </div>
)

export default PartnersPage
